import React, { useEffect, useState } from 'react'
import Icon from 'srcReact/components/icons/Icon'
import { getDateFormat1, getDateFormat2, getTimeFormat1, isToday, isTomorrow } from 'srcReact/helpers/datetimeHelper'
import styles from './styles.module.scss'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { decode } from 'html-entities/lib/index'
import Button from 'srcReact/components/common2/Button'
import { openInNewTab } from 'srcReact/helpers/utils'
import { dateDiffInDays, formatLastUpdateDate, showTwoFormattedDates } from 'srcReact/helpers/datetimeHelper'
import Tag from 'srcReact/components/common2/Tag'

const Header = ({
  mixr,
  isMemberViewVisible,
  setShowAddToCalendarPopup,
  isGoing,
  isMobile,
  isDesktop,
  isSuspended,
  handleAddToCalendar
}) => {
  const { t } = useTranslation()
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const [timeZone, setTimeZone] = useState('')
  const [picture, setPicture] = useState(null)
  const [recurrenceType, setRecurrenceType] = useState('')
  const [utcOffset, setUtcOffset] = useState('')
  const [groupPrivacy, setGroupPrivacy] = useState('')
  const [mixrDate, setMixrDate] = useState('')

  useEffect(() => {
    if (mixr) {
      const { local_start_at, local_end_at, location, recurrence, picture, group } = mixr
      let startDate = local_start_at
      let endDate = local_end_at
      if (mixr.is_preview && recurrence?.type !== 'none' && recurrence?.events?.length > 0) {
        //if mixr is in preview state, then show recurrent mixrs' first event data in header
        startDate = recurrence.events[0].start_at || startDate
        endDate = recurrence.events[0].end_at || endDate
      }
      const dtStartDate = new Date(startDate)
      const dtEndDate = new Date(endDate)
      setStartDate(dtStartDate)
      setEndDate(dtEndDate)
      if (location) {
        setTimeZone(location.timezone.timeZoneAbbreviation)
        setUtcOffset(location.timezone.UTCOffset || location.timezone.utc_offset)
      }
      setPicture(picture)
      setRecurrenceType(recurrence && recurrence.type)

      setGroupPrivacy(group && (group.privacy_setting || group.group_privacy_setting))
      const daysDiff = dateDiffInDays(dtEndDate, dtStartDate)
      if (isToday(dtStartDate)) {
        setMixrDate(
          `${t('global.today')}${daysDiff > 0 ? ` - ${formatLastUpdateDate(dtEndDate, getDateFormat2())}` : ''}`
        )
      } else if (isTomorrow(dtStartDate)) {
        setMixrDate(
          `${t('global.tomorrow')}${daysDiff > 0 ? ` - ${formatLastUpdateDate(dtEndDate, getDateFormat2())}` : ''}`
        )
      } else {
        if (daysDiff > 0) {
          setMixrDate(showTwoFormattedDates(dtStartDate, dtEndDate, getDateFormat2()))
        } else {
          setMixrDate(formatLastUpdateDate(dtStartDate, getDateFormat1()))
        }
      }
    }
  }, [mixr])

  const renderFees = (fees) => {
    let higherPrice = fees[0]
    let smallerPrice = fees[fees.length - 1]
    if (fees.length === 1) {
      return `${higherPrice.amount}${higherPrice.currency_symbol}`
    }
    return `${higherPrice.amount}${higherPrice.currency_symbol} | ${smallerPrice.amount}${smallerPrice.currency_symbol}`
  }

  const handleFeesClick = (link) => {
    if (link) openInNewTab(link)
  }
  const isInvalidMixr = mixr && (mixr.status === 'over' || mixr.status === 'cancelled')

  return (
    <div
      className={`${styles.headerContainer} ${
        isMemberViewVisible ? styles.headerContainer1 : styles.headerContainer2
      } ${isSuspended && styles.suspendedHeader}`}>
      <div
        className={`${styles.cardHeader} ${styles.mixrHeader} ${
          isInvalidMixr ? styles.gray : mixr && mixr.color ? styles[mixr.color] : styles.green
        }`}>
        {recurrenceType !== 'none' && <Icon name='recurring-mixrs' styleModifier={styles.recurringIcon} />}
        <div className={styles.date}>{mixrDate}</div>
        <div className={styles.time}>
          {`${dayjs(startDate).format(getTimeFormat1())} - ${dayjs(endDate).format(getTimeFormat1())}`}{' '}
          <span>{timeZone}</span>
        </div>
        {groupPrivacy !== 'public' && <Icon name='mask' styleModifier={styles.maskIcon} />}
      </div>

      <div
        className={`${styles.banner}`}
        style={
          picture?.url?.length > 0
            ? {
                background: `url(${picture.url}) 50% 50% / cover no-repeat`,
                backgroundSize: 'cover'
              }
            : mixr?.group?.picture?.url
            ? {
                background: `url(${mixr.group.picture.url}) 50% 50% / cover no-repeat`,
                backgroundSize: 'cover'
              }
            : {
                background: `url(${config.assetUrl + '/images/background-default-mixr.jpeg'}) 50% 50% no-repeat`,
                backgroundSize: 'cover'
              }
        }>
        {isInvalidMixr ? (
          <div className={`${styles.mixrOver} ${mixr.status === 'cancelled' && styles.mixrCancelled}`}>
            <h2 className={styles.mixrStatus}>
              {mixr.status === 'cancelled' ? t('mixrHome.feeds.mixrCancelled') : t('mixrHome.feeds.mixrStatus')}
            </h2>
            <h2 className={styles.heading}>{mixr && decode(mixr.name)}</h2>
          </div>
        ) : (
          <h2 className={styles.heading}>{mixr && decode(mixr.name)}</h2>
        )}
        {mixr?.group?.type === 'expertise' ? (
          <Tag
            text={t('group.createGroup.groupType.expertise.titleMixr')}
            color='purple'
            position='left'
            className={styles.expertiseTag}
          />
        ) : null}
        {isDesktop && mixr.status !== 'cancelled' && (
          <div className={`${styles.ctaButtons} ${mixr && mixr.is_preview && styles.inactiveButton}`}>
            {mixr && mixr.status !== 'over' && isGoing && !isSuspended && (
              <Button
                text={t('mixrHome.addToCalendar')}
                customStyle={styles.calendarBtn}
                size='large'
                iconPosition='start'
                icon={<Icon name='create-mixr' size='large32' />}
                handleClick={() => {
                  handleAddToCalendar()
                  // if (setShowAddToCalendarPopup) {
                  //   setShowAddToCalendarPopup(true)
                  // }
                }}
              />
            )}
            {mixr && mixr.pricing_type === 'with_fees' && mixr.fees && mixr.fees.length >= 1 && (
              <Button
                text={renderFees(mixr.fees)}
                customStyle={styles.priceBtn}
                size='large'
                iconPosition='start'
                icon={<Icon name='price-icon' size='large32' />}
                {...(mixr.fees[0].link ? { icon2: <Icon name='arrow-right' size='large32' /> } : {})}
                handleClick={() => mixr && !mixr.is_preview && handleFeesClick(mixr.fees[0].link)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default Header
